import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db, auth } from '../../firebase/config'; 
import { doc } from 'firebase/firestore';

// Importamos la utilidad de persistencia, limpieza y contexto maestro
import { useSegurosData } from '../../context/SegurosDataContext';
import { saveToFirestore } from '../../firebase/cleanPayload';

// Importación del Custom Hook de Bitácoras Centralizado (Pilar del Canvas)
import { useBitacoraTimeline } from '../../utils/useBitacoraTimeline';

// Componentes, Listas y Estilos
import { etapasPostVentaList } from '../../data/listasDesplegables';
import { getPersonaChipClass } from '../../utils/styles';

/**
 * ModalPostVenta - Gestiona el seguimiento de postventa de una póliza.
 * Refactorizado bajo arquitectura limpia con el custom hook unificado useBitacoraTimeline sin duplicados.
 */
const ModalPostVenta = ({ show, onClose, poliza, onSuccess, initialData }) => {
    // --- 1. CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
    const { dbMasters } = useSegurosData();

    // --- 2. ESTADOS LOCALES ---
    const [stageForm, setStageForm] = useState({
        fechaCreacion: new Date().toISOString().split('T')[0], // Input YYYY-MM-DD
        tipoGestion: '',
        quienGestiono: '',
        comentarios: '',
        fechaProximaGestion: ''
    });

    const [estadoGlobal, setEstadoGlobal] = useState('Abierto');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const polizaPath = poliza?.path;

    // --- 3. INICIALIZACIÓN DEL HOOK CENTRAL DE BITÁCORAS ---
    // Pasamos 'comentario' para mapear dinámicamente la propiedad en el gestor de postventa
    const {
        items: historialLocal,
        loading: loadingHistory,
        editingId: editingLocalId,
        setEditingId: setEditingLocalId,
        fetchNotes: fetchHistorial,
        addLocalNote,
        updateLocalNote,
        removeLocalNote,
        persistBatchChanges
    } = useBitacoraTimeline('comentario');

    // --- 4. CARGA INICIAL DEL HISTORIAL CON EL HOOK ---
    useEffect(() => {
        if (!show || !polizaPath) return;

        // Sincronizar estado global
        setEstadoGlobal(poliza.postventa_estado || 'Abierto');

        // Cargar el historial con el Hook unificado
        fetchHistorial(`${polizaPath}/gestiones_postventa`);

        // Si viene data inicial para editar una etapa específica desde el exterior
        if (initialData) {
            setEditingLocalId(initialData.id);
            const fCreacionInitial = initialData.fechaCreacion?.seconds 
                ? new Date(initialData.fechaCreacion.seconds * 1000).toISOString()
                : initialData.fechaCreacion;

            setStageForm({
                fechaCreacion: fCreacionInitial ? fCreacionInitial.split('T')[0] : new Date().toISOString().split('T')[0],
                tipoGestion: initialData.tipoGestion || '',
                quienGestiono: initialData.quienGestiono || '',
                comentarios: initialData.comentario || initialData.comentarios || initialData.observacion || '',
                fechaProximaGestion: initialData.fechaProximaGestion || ''
            });
        }
    }, [show, polizaPath, initialData, poliza.postventa_estado, fetchHistorial, setEditingLocalId]);

    // --- 5. MEMORIZACIÓN Y ORDENAMIENTO EN CACHÉ LOCAL (useMemo) ---
    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    const sortedEtapas = useMemo(() => {
        return [...(etapasPostVentaList || [])].sort((a, b) => a.localeCompare(b));
    }, []);

    // --- 6. ACCIONES Y MANIPULACIÓN DEL TIMELINE ---
    const handleStageChange = (e) => {
        const { name, value } = e.target;
        setStageForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddOrUpdateStage = (e) => {
        e.preventDefault();
        if (!stageForm.tipoGestion || !stageForm.quienGestiono) return;

        // Construimos la fecha con la hora actual para no perder la precisión del orden temporal
        const now = new Date();
        const [y, m, d] = stageForm.fechaCreacion.split('-').map(Number);
        const dateWithCurrentTime = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds());

        // ◄ SOLUCIÓN DUPLICADOS: Dejamos únicamente los metadatos comerciales extras.
        // El Hook se encarga de inyectar 'tipo' y 'comentario' de forma limpia y transparente.
        const extraFields = {
            quienGestiono: stageForm.quienGestiono,
            fechaProximaGestion: stageForm.fechaProximaGestion || '',
            fechaCreacion: dateWithCurrentTime
        };

        if (editingLocalId !== null) {
            updateLocalNote(editingLocalId, stageForm.tipoGestion, stageForm.comentarios, extraFields);
            setEditingLocalId(null);
        } else {
            addLocalNote(stageForm.tipoGestion, stageForm.comentarios, extraFields);
        }

        // Limpiar inputs
        setStageForm({
            fechaCreacion: new Date().toISOString().split('T')[0],
            tipoGestion: '',
            quienGestiono: '',
            comentarios: '',
            fechaProximaGestion: ''
        });
    };

    const handleEditEtapa = (h) => {
        setEditingLocalId(h.id || h.localId);
        
        const rawDate = h.fechaCreacion;
        let dateStr = new Date().toISOString().split('T')[0];
        
        if (typeof rawDate === 'string') {
            dateStr = rawDate.split('T')[0];
        } else if (rawDate?.seconds) {
            dateStr = new Date(rawDate.seconds * 1000).toISOString().split('T')[0];
        }

        setStageForm({
            fechaCreacion: dateStr,
            tipoGestion: h.tipoGestion || '',
            quienGestiono: h.quienGestiono || '',
            comentarios: h.comentario || h.comentarios || h.observacion || '',
            fechaProximaGestion: h.fechaProximaGestion || ''
        });
    };

    const handleRemoveLocal = (h) => {
        removeLocalNote(h.id || h.localId, `${polizaPath}/gestiones_postventa`);
        
        if (editingLocalId === (h.id || h.localId)) {
            setEditingLocalId(null);
            setStageForm({ 
                fechaCreacion: new Date().toISOString().split('T')[0], 
                tipoGestion: '', 
                quienGestiono: '', 
                comentarios: '', 
                fechaProximaGestion: '' 
            });
        }
    };

    // --- 7. PROCESAMIENTO Y CONFIRMACIÓN TRANSACCIONAL EN BATCH ---
    const handleFinalSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const gestionesPath = `${polizaPath}/gestiones_postventa`;

            // A. Guardamos de forma masiva en lote todas las etapas modificadas o creadas
            await persistBatchChanges(gestionesPath);

            // B. Ordenamos localmente para extraer los campos denormalizados de la última gestión
            const sortedHistorial = [...historialLocal].sort((a, b) => {
                const dateA = a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0);
                const dateB = b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0);
                return dateB - dateA;
            });

            const ultimaEtapa = sortedHistorial[0] || {};
            const numeroGestionFinal = poliza.postventa_numero_gestion || `PV-${Math.floor(10000 + Math.random() * 90000)}`;

            const polizaUpdateData = {
                postventa: "Si",
                postventa_estado: estadoGlobal, 
                postventa_numero_gestion: numeroGestionFinal,
                postventa_fechaProximaGestion: ultimaEtapa.fechaProximaGestion || '',
                postventa_quienGestiono: ultimaEtapa.quienGestiono || poliza.postventa_quienGestiono || '',
                postventa_ultimoTipoGestion: ultimaEtapa.tipoGestion || poliza.postventa_ultimoTipoGestion || ''
            };

            const pathParts = polizaPath.split('/');
            const polizaCollection = pathParts.slice(0, -1).join('/');
            const polizaId = pathParts[pathParts.length - 1];

            // C. Guardamos la actualización de control en la póliza raíz
            await saveToFirestore(polizaCollection, polizaUpdateData, polizaId);

            onSuccess();
        } catch (error) {
            console.error("Error crítico al confirmar gestión de postventa:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show || !poliza) return null;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-auto max-h-[95vh] flex flex-col overflow-hidden border border-gray-100 animate-fade-in text-left font-sans transition-all duration-300">
                
                {/* CABECERA DEL MODAL */}
                <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 shrink-0 bg-white select-none">
                    <div className="text-left">
                        <div className="flex items-center gap-3">
                            <h3 className="text-xl font-bold text-gray-800 uppercase tracking-tight">Gestión de PostVenta</h3>
                            {poliza.postventa_numero_gestion && (
                                <span className="bg-indigo-50 text-indigo-600 px-3 py-0.5 rounded-lg border border-indigo-100 text-[10px] font-black tracking-widest uppercase">
                                    Ref: {poliza.postventa_numero_gestion}
                                </span>
                            )}
                        </div>
                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mt-2">Póliza #{poliza.nPoliza} • {poliza.compania} • {poliza.displayNombre || poliza.clienteNombre}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-50 rounded-full">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* CONTENIDO SCROLLABLE */}
                <div className="overflow-y-auto p-8 flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent space-y-8 text-left">
                    
                    {/* ESTADO DE LA GESTIÓN */}
                    <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-slate-50/10 shadow-sm">
                        <legend className="px-3 text-base font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">Estado de la Gestión</legend>
                        <div className="flex items-center gap-6 pt-2 select-none">
                            <div className="flex-grow text-left">
                                <p className="text-xs text-slate-500 italic">Marca "Cerrado" para archivar el caso en el tablero.</p>
                            </div>
                            <div className="flex bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
                                {['Abierto', 'Cerrado'].map(est => (
                                    <button
                                        key={est}
                                        type="button"
                                        onClick={() => setEstadoGlobal(est)}
                                        className={`px-8 py-2 rounded-lg font-black text-[10px] uppercase tracking-widest transition-all ${estadoGlobal === est ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-indigo-600'}`}
                                    >
                                        {est}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </fieldset>

                    {/* FORMULARIO DE CARGA DE ACCIÓN */}
                    <fieldset className={`border rounded-xl p-6 relative transition-colors shadow-sm duration-300 ${editingLocalId ? 'border-orange-300 bg-orange-50/10' : 'border-indigo-200 bg-indigo-50/10'}`}>
                        <legend className={`px-3 text-base font-bold bg-white absolute -top-3.5 left-4 tracking-tight ${editingLocalId ? 'text-orange-700' : 'text-indigo-700'}`}>
                            {editingLocalId ? 'Modificar Registro' : 'Cargar Etapa de Seguimiento'}
                        </legend>
                        
                        <form onSubmit={handleAddOrUpdateStage} className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                            <div>
                                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1 select-none">Fecha Gestión</label>
                                <input type="date" name="fechaCreacion" value={stageForm.fechaCreacion} onChange={handleStageChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[46px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all text-left" required />
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Etapa / Acción Realizada</label>
                                <select name="tipoGestion" value={stageForm.tipoGestion} onChange={handleStageChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white h-[46px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all text-left" required>
                                    <option value="">Seleccione etapa...</option>
                                    {sortedEtapas.map(et => <option key={et} value={et}>{et}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">¿Quién gestionó?</label>
                                <select name="quienGestiono" value={stageForm.quienGestiono} onChange={handleStageChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[46px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all text-left" required>
                                    <option value="">Seleccione colaborador...</option>
                                    {sortedColaboradores.map(col => <option key={col.id} value={col.nombre}>{col.nombre}</option>)}
                                </select>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Observaciones de la Etapa</label>
                                <textarea 
                                    name="comentarios" 
                                    value={stageForm.comentarios} 
                                    onChange={handleStageChange} 
                                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 outline-none bg-white min-h-[100px] resize-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all placeholder:text-slate-300 text-left" 
                                    placeholder="Detalle los avances o el resultado de la gestión..."
                                    required
                                />
                            </div>

                            <div className="flex flex-col justify-between">
                                <div>
                                    <label className="block text-[11px] font-bold text-indigo-700 uppercase tracking-wide mb-1.5 pl-1 select-none">Próxima Gestión</label>
                                    <input type="date" name="fechaProximaGestion" value={stageForm.fechaProximaGestion} onChange={handleStageChange} className="w-full border border-indigo-200 rounded-xl px-4 py-2.5 text-sm font-medium text-indigo-900 outline-none bg-white h-[46px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all text-left" />
                                </div>
                                <div className="flex gap-2">
                                    {editingLocalId !== null && (
                                        <button 
                                            type="button" 
                                            onClick={() => { setEditingLocalId(null); setStageForm({ fechaCreacion: new Date().toISOString().split('T')[0], tipoGestion: '', quienGestiono: '', comentarios: '', fechaProximaGestion: '' }); }}
                                            className="flex-1 mt-4 bg-slate-100 text-slate-500 font-bold uppercase text-xs rounded-xl hover:bg-slate-200 transition-all transform active:scale-95 shadow-sm py-3"
                                        >
                                            Cancelar
                                        </button>
                                    )}
                                    <button 
                                        type="submit" 
                                        className={`mt-4 text-white font-bold uppercase text-xs rounded-xl transition-all shadow-lg transform active:scale-95 py-3 ${editingLocalId !== null ? 'bg-orange-600 hover:bg-orange-700 shadow-orange-100 flex-1' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100 w-full'}`}
                                    >
                                        {editingLocalId !== null ? 'Actualizar' : '+ Agregar'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </fieldset>

                    {/* VISTA PREVIA DEL HISTORIAL */}
                    <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                        <legend className="px-3 text-base font-bold text-slate-700 bg-white absolute -top-3.5 left-4 flex items-center gap-2 select-none">
                            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            Historial de Seguimiento (Vista Previa)
                        </legend>

                        <div className="mt-4 space-y-4">
                            {loadingHistory ? (
                                <p className="text-center py-8 text-slate-400 font-bold text-xs animate-pulse uppercase tracking-widest select-none">Sincronizando historial...</p>
                            ) : historialLocal.length === 0 ? (
                                <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-slate-200 text-slate-400 text-sm italic select-none">Sin registros para este caso.</div>
                            ) : (
                                historialLocal.map((h) => (
                                    <div key={h.id || h.localId} className={`group bg-white border rounded-2xl p-4 shadow-sm transition-all flex flex-col md:flex-row md:items-center gap-4 relative overflow-hidden ${editingLocalId === (h.id || h.localId) ? 'border-orange-400 bg-orange-50/50 ring-2 ring-orange-100' : 'border-slate-100 hover:border-indigo-200'}`}>
                                        <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors select-none ${editingLocalId === (h.id || h.localId) ? 'bg-orange-500' : h.isNew ? 'bg-emerald-400' : h.isModified ? 'bg-blue-400' : 'bg-slate-100'}`}></div>
                                        
                                        <div className="min-w-[130px] text-center md:text-left select-none">
                                            <span className="text-[10px] font-black text-slate-400 tabular-nums uppercase">
                                                {new Date(h.fechaCreacion).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}
                                            </span>
                                        </div>

                                        <div className="min-w-[180px] text-left select-none">
                                            <span className="text-xs font-black text-indigo-700 uppercase tracking-tight">{h.tipoGestion}</span>
                                            {h.isNew && <span className="ml-2 text-[8px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100 uppercase tracking-tighter">Nueva</span>}
                                            {h.isModified && <span className="ml-2 text-[8px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 uppercase tracking-tighter">Editada</span>}
                                        </div>

                                        <div className="flex-grow text-left">
                                            <p className="text-sm text-slate-600 italic leading-relaxed pr-4">"{h.comentario || h.comentarios || h.observacion}"</p>
                                        </div>

                                        <div className="flex items-center gap-3 shrink-0 select-none">
                                            <div className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase ${getPersonaChipClass(h.quienGestiono)}`}>
                                                {h.quienGestiono}
                                            </div>
                                            <div className="flex gap-1 md:opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                <button onClick={() => handleEditEtapa(h)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z" /></svg></button>
                                                <button onClick={() => handleRemoveLocal(h)} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </fieldset>
                </div>

                {/* BOTONERA DE ACCIÓN INFERIOR */}
                <div className="px-8 py-5 border-t border-slate-100 flex justify-end gap-3 bg-slate-50 shrink-0 select-none">
                    <button onClick={onClose} className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 shadow-sm transition-all transform active:scale-95">Cancelar</button>
                    <button 
                        onClick={handleFinalSubmit}
                        disabled={isSubmitting}
                        className="px-10 py-2.5 rounded-full bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 text-xs uppercase tracking-wider transform active:scale-95 disabled:opacity-50 min-w-[180px]"
                    >
                        {isSubmitting ? 'Guardando...' : 'Confirmar Todo'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default ModalPostVenta;